import {
  CREDIT_RATES,
  IMAGE_MODES,
  MAX_TEXT_LENGTH,
  countCharacters,
  countWords,
  type ImageMode,
  type TextMethod,
} from '@lyrmo/shared';
import { ariaShortcut, formatShortcut } from '@lyrmo/shared/shortcuts';
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { ApiError } from '../../api/api-error';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { RollingNumber } from '../../components/RollingNumber';
import { Toast } from '../../components/Toast';
import { useCountingNumber } from '../../hooks/useCountingNumber';
import { useClipboard } from '../../hooks/useClipboard';
import { useOneLine } from '../../hooks/useOneLine';
import { useWidthGlide } from '../../hooks/useWidthGlide';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { motionAllowed, withViewTransition } from '../../lib/motion';
import { modifierKey } from '../../lib/platform';
import { useTrack } from '../../lib/tracking-context';
import { useShortcuts } from '../shortcuts/useShortcuts';
import type { SpeechState } from './useSpeech';
import {
  IMPORT_ACCEPT,
  importProblem,
  isImageImport,
  readImport,
  type ExtractImageText,
} from './import-file';
import { FileName } from './FileName';
import { closeToSpark, leapTo, paceFor, playLitCaret } from './image-progress';
import { ImageProgress, ImageThumb } from './ImageProgress';
import { insertTranscript } from './insert-transcript';
import { insertText } from './markdown-edit';
import { copyFormatted } from './markdown-html';
import { playTool } from './tool-motion';
import { playSendOff } from './send-off';
import { useWritingAids } from './useWritingAids';
import { VoiceWave } from './VoiceWave';
import { WritingAids } from './WritingAids';
import { WritingField, type DraftField, type TextMark } from './WritingField';
import './input-panel.css';

/**
 * How the tools under the draft make room to stay on one line, each step on top of the last: the
 * names go, the least used first, then the menus' chevrons and the gaps, then the icons shrink.
 */
const TOOLS_FIT = ['aids', 'ai-tools', 'dictation', 'compact', 'small'] as const;
/** The header's steps to one line: Shortcuts and Full screen as icons, then every button. */
const HEADER_FIT = ['compact', 'icons'] as const;
/** The keys that answer what to do with images that came in. */
const IMAGE_MODE_KEYS: Readonly<Record<ImageMode, string>> = { text: 'T', describe: 'D' };
const IMAGE_KEYS: Readonly<Partial<Record<string, ImageMode>>> = { t: 'text', d: 'describe' };

interface InputPanelProps {
  readonly readOnly?: boolean;
  readonly value: string;
  readonly onChange: (value: string, typing?: boolean) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onSubmit: () => void;
  readonly onClear: () => void;
  readonly busy: boolean;
  /**
   * Clear takes the rewrites too, which are showing or on their way: it's there to use even once
   * the draft is empty. Otherwise it takes the draft alone.
   */
  readonly clearsResults?: boolean;
  readonly canSubmit: boolean;
  /** Seconds left before the rate limit allows another rewrite. */
  readonly cooldownSeconds: number;
  /** Words left in today's allowance, when known. */
  readonly creditsLeft?: number | undefined;
  /** Hosts that show the balance in their header can omit the duplicate credit counter. */
  readonly showCreditCounter?: boolean | undefined;
  readonly fieldRef: RefObject<DraftField | null>;
  /** Controls along the top of the editor, such as the rewrite options. */
  readonly toolbar?: ReactNode;
  /** Beside the heading, such as what the draft is saved as. */
  readonly heading?: ReactNode;
  /** Before the header's own buttons, such as Drafts. */
  readonly headerActions?: ReactNode;
  readonly dictation?: ReactNode;
  readonly extractImageText?: ExtractImageText | undefined;
  /** Cancels image imports when the account changes. */
  readonly importAccount?: string | undefined;
  /** The menu of AI tools that format the draft; omitted where there are none. */
  readonly aiTools?: ReactNode;
  /** Part of the draft to mark, such as what an AI tool is formatting. */
  readonly mark?: TextMark | null | undefined;
  /** The formatting toolbar over the draft; on unless said otherwise. */
  readonly formatting?: boolean | undefined;
  /** The draft's voice while it's read aloud, or its error; null otherwise. */
  readonly speech?: SpeechState | undefined;
  /** Reads the draft aloud; omitted where there's no voice. */
  readonly onSpeak?: (() => void) | undefined;
  readonly onStopSpeech?: (() => void) | undefined;
  /** Opens the keyboard shortcuts. */
  readonly onShowShortcuts?: (() => void) | undefined;
  /** Host integrations keep the shared editor's UI and use native clipboard/window controls. */
  readonly readClipboard?: (() => Promise<string>) | undefined;
  readonly writeClipboard?: ((text: string) => void | Promise<void>) | undefined;
  readonly expanded?: boolean | undefined;
  readonly onExpandedChange?: ((expanded: boolean) => void) | undefined;
  /** Hosts with native window controls can hide the editor's expansion button. */
  readonly showExpandButton?: boolean | undefined;
  /**
   * Pastes and drops anywhere on the page come into the draft, and typing with nothing focused
   * starts in it: for a page that's the editor's, not for one it's laid over.
   */
  readonly pageWide?: boolean | undefined;
  readonly actions?: ReactNode;
}

/**
 * The draft with an image's writing in place of `range`, the selection or caret, fitted in as
 * dictated words are: spaced from the words on either side, and the caret after it.
 */
function addImageWriting(draft: string, writing: string, [start, end]: readonly [number, number]) {
  const added = insertTranscript(draft, writing, { start, end });
  return { text: added.text, selection: [added.caret, added.caret] as const };
}

/** Today's credits left, counting to a new value as in the header. Apart, so only it redraws. */
function CreditsLeft({ credits }: { readonly credits: number }) {
  const { t } = useI18n();
  return t.editor.creditsLeftToday(useCountingNumber(credits));
}

export function InputPanel({
  readOnly = false,
  value,
  onChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSubmit,
  onClear,
  busy,
  clearsResults = false,
  canSubmit,
  cooldownSeconds,
  creditsLeft,
  showCreditCounter = true,
  fieldRef,
  toolbar,
  heading,
  headerActions,
  dictation,
  extractImageText,
  importAccount,
  aiTools,
  mark,
  formatting = true,
  speech,
  onSpeak,
  onStopSpeech,
  onShowShortcuts,
  readClipboard,
  writeClipboard,
  expanded: hostExpanded,
  onExpandedChange,
  showExpandButton = true,
  pageWide = false,
  actions,
}: InputPanelProps) {
  const { t, locale, format, describeError } = useI18n();
  const { platform, shortcuts } = useShortcuts();
  const [localExpanded, setExpanded] = useState(false);
  const expanded = hostExpanded ?? localExpanded;
  const importSession = useRef<AbortController | null>(null);
  const [importing, setImporting] = useState<{
    controller: AbortController;
    source: ExtractImageText | undefined;
    account: string | undefined;
    image: boolean;
    file: File;
    fileName: string;
    mode: ImageMode | undefined;
    /** Where the choice was pressed, for the progress to grow out of it. */
    from: DOMRect | null;
  } | null>(null);
  const isImporting =
    importing !== null &&
    importing.source === extractImageText &&
    importing.account === importAccount &&
    !readOnly &&
    !importing.controller.signal.aborted;
  useEffect(
    () => () => {
      importSession.current?.abort();
    },
    [extractImageText, importAccount, readOnly],
  );
  /** Images waiting for the writer to choose what to do with them, and where they're to go. */
  const [choice, setChoice] = useState<{
    files: readonly File[];
    draft: string;
    range: readonly [number, number];
    source: ExtractImageText | undefined;
    account: string | undefined;
    /** Their text was asked for, but there was none: describing them is offered first. */
    noText: boolean;
  } | null>(null);
  // Like an import under way, a choice is for the account and service it was asked of.
  const isChoosing =
    choice !== null &&
    choice.source === extractImageText &&
    choice.account === importAccount &&
    !readOnly;
  // The question takes the focus, so its keys work at once, without either answer put first.
  const choiceRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isChoosing) choiceRef.current?.focus();
  }, [isChoosing, choice]);
  const choiceImages = choice?.files.filter(isImageImport) ?? [];
  const imageCredits = choiceImages.length * CREDIT_RATES.imagePerImage;
  // The progress an image's reading grows into, and closes to a spark from (ImageProgress.tsx).
  const progressRef = useRef<HTMLDivElement>(null);
  /**
   * An image's writing, put in as the spark carrying it lands: marked as done at its own pace, so
   * it's written in word by word behind a lit caret (image-progress.ts, input-panel.css), and
   * marked only while the draft still has it.
   */
  const [landing, setLanding] = useState<{
    start: number;
    end: number;
    text: string;
    since: number;
    pace: number;
    caret: readonly [number, number];
    spark: HTMLElement | null;
  } | null>(null);
  const imageMark: TextMark | null =
    landing && value.startsWith(landing.text, landing.start)
      ? {
          start: landing.start,
          end: landing.end,
          state: 'done',
          since: landing.since,
          pace: landing.pace,
        }
      : null;
  useEffect(() => {
    if (!landing) return;
    const field = fieldRef.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
      if (field && fieldRef.current === field) {
        field.focus();
        field.setSelectionRange(landing.caret[0], landing.caret[1]);
      }
      timer = setTimeout(
        () => {
          setLanding(null);
        },
        playLitCaret(field, landing.spark, landing.since, landing.pace),
      );
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [landing, fieldRef]);
  const fileRef = useRef<HTMLInputElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  useOneLine(toolsRef, TOOLS_FIT);
  const headerRef = useRef<HTMLDivElement>(null);
  useOneLine(headerRef, HEADER_FIT);
  const [pasting, setPasting] = useState(false);
  const [notice, setNotice] = useState<{ id: number; message: string } | null>(null);
  const noticeCount = useRef(0);
  const dismissNotice = useCallback(() => {
    setNotice(null);
  }, []);
  const clipboard = useClipboard<'draft'>(1500, writeClipboard ?? copyFormatted);
  const characters = countCharacters(value);
  const overLimit = characters > MAX_TEXT_LENGTH;
  const usage = Math.min(characters / MAX_TEXT_LENGTH, 1);
  const words = countWords(value);
  // A hint, not a block: the server has the final say, and the day may have rolled over.
  const overAllowance =
    creditsLeft !== undefined && words * CREDIT_RATES.rewritePerWord > creditsLeft;
  const speaking = speech?.status === 'loading' || speech?.status === 'playing';
  const speechCredits = words * CREDIT_RATES.speechPerWord;
  const writingAids = useWritingAids();

  // While rewriting, the button stays Rewrite, at work (input-panel.css); the versions say what's
  // happening. Rewrite and Wait take their turns smoothly: the button glides to its new width,
  // and the new label rises into it.
  const waiting = cooldownSeconds > 0;
  const submitLabel = waiting ? t.editor.wait(format.duration(cooldownSeconds)) : t.editor.rewrite;
  const submitRef = useRef<HTMLButtonElement>(null);
  useWidthGlide(submitRef, submitLabel);
  // Each rewrite sets off with a flourish from Rewrite, however it was asked for (send-off.ts).
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (busy && !wasBusy.current) playSendOff(submitRef.current);
    wasBusy.current = busy;
  }, [busy]);

  const track = useTrack();
  /** Notes text coming into the editor while it was empty, and how it came. */
  function textIn(before: string, after: string, method: TextMethod) {
    if (before.trim() || !after.trim()) return;
    track({ event: 'text', method, words: countWords(after) });
  }

  /** Shows a toast; repeating a message still shows it afresh. */
  function notify(message: string) {
    noticeCount.current += 1;
    setNotice({ id: noticeCount.current, message });
  }

  function cancelImport() {
    importSession.current?.abort();
    importSession.current = null;
    setImporting(null);
  }

  /**
   * Back or forward through the draft's history, as a little time travel: the draft slides
   * away, and the one from before, or after, slides in from the other side (input-panel.css).
   */
  function travel(action: () => void, direction: 'undo' | 'redo') {
    if (readOnly) return;
    playTool(toolsRef.current, direction);
    withViewTransition(direction, action, { join: true });
    fieldRef.current?.focus();
  }

  /**
   * Puts the writing in files, chosen with Import, pasted, or dropped, in the draft in place of
   * `at`, or else the selection, as one edit that Undo takes back. An image's writing goes in as
   * dictation's words do: in place of the selection, or at the caret, fitted in. Unless it can
   * take them all, it takes none, and says why.
   */
  async function importFiles(files: readonly File[], at?: readonly [number, number]) {
    const field = fieldRef.current;
    if (!field || readOnly || files.length === 0) return;
    if (importSession.current && !importSession.current.signal.aborted) return;
    const problem = files.map(importProblem).find((found) => found !== null);
    if (problem) {
      notify(t.editor.feedback[problem]);
      return;
    }
    const draft = field.value;
    const range = at ?? [field.selectionStart, field.selectionEnd];
    // Images wait for the writer to say whether to read their text or describe them.
    if (extractImageText && files.some(isImageImport)) {
      setChoice({
        files,
        draft,
        range,
        source: extractImageText,
        account: importAccount,
        noText: false,
      });
      return;
    }
    await readFiles(files, draft, range);
  }

  /** Reads or describes the images waiting for a choice, unless the draft changed meanwhile. */
  function choose(mode: ImageMode) {
    if (!isChoosing) return;
    // The choice pressed grows into the progress, from where it is now.
    const from =
      choiceRef.current?.querySelector(`[data-mode="${mode}"]`)?.getBoundingClientRect() ?? null;
    setChoice(null);
    if (fieldRef.current?.value !== choice.draft) {
      notify(t.editor.feedback.importChanged);
      return;
    }
    void readFiles(choice.files, choice.draft, choice.range, mode, from);
  }

  function cancelChoice() {
    setChoice(null);
    fieldRef.current?.focus();
  }

  async function readFiles(
    files: readonly File[],
    draft: string,
    range: readonly [number, number],
    imageMode?: ImageMode,
    from: DOMRect | null = null,
  ) {
    const field = fieldRef.current;
    if (!field || readOnly) return;
    if (importSession.current && !importSession.current.signal.aborted) return;
    const controller = new AbortController();
    importSession.current = controller;
    try {
      // Run in order so a multi-image import cannot send an unbounded burst of OCR requests.
      const read = [];
      for (const file of files) {
        if (controller.signal.aborted) return;
        setImporting({
          controller,
          source: extractImageText,
          account: importAccount,
          image: isImageImport(file),
          file,
          fileName: file.name,
          mode: imageMode,
          from,
        });
        read.push(
          await readImport(file, {
            extractImageText,
            imageMode,
            language: locale,
            signal: controller.signal,
          }),
        );
      }
      if (controller.signal.aborted || fieldRef.current !== field) return;
      if (field.value !== draft) {
        notify(t.editor.feedback.importChanged);
        return;
      }
      const texts: string[] = [];
      for (const result of read) {
        if ('problem' in result) {
          notify(t.editor.feedback[result.problem]);
          return;
        }
        texts.push(result.text);
      }
      const writing = texts.join('\n\n');
      const edit = imageMode
        ? addImageWriting(draft, writing, range)
        : insertText(draft, range, writing);
      if (imageMode && motionAllowed()) {
        // An image's progress closes to a spark, which leaps to where its writing is to go; only
        // once it's there does the writing go in. It can be cancelled, or the draft changed, meanwhile.
        const cancelled = () => controller.signal.aborted || fieldRef.current !== field;
        const stale = (spark: HTMLElement | null) => {
          if (!cancelled() && field.value === draft) return false;
          spark?.remove();
          if (!cancelled()) notify(t.editor.feedback.importChanged);
          return true;
        };
        const spark = await closeToSpark(progressRef.current);
        if (stale(spark)) return;
        field.focus();
        field.setSelectionRange(range[0], range[1]);
        const since = await leapTo(spark, field, range[0]);
        if (stale(spark)) return;
        textIn(draft, edit.text, 'file');
        setLanding({
          start: range[0],
          end: edit.selection[0],
          text: edit.text.slice(range[0], edit.selection[0]),
          since,
          pace: paceFor(countWords(writing)),
          caret: edit.selection,
          spark,
        });
        onChange(edit.text);
        notify(t.editor.feedback.imported(files.map((file) => file.name).join(', ')));
        return;
      }
      // The files' writing comes up into the draft, as a paste does, and the caret goes after it.
      textIn(draft, edit.text, 'file');
      withViewTransition(
        'paste',
        () => {
          onChange(edit.text);
        },
        {
          after: () => {
            requestAnimationFrame(() => {
              if (fieldRef.current !== field) return;
              field.focus();
              field.setSelectionRange(edit.selection[0], edit.selection[1]);
            });
          },
        },
      );
      notify(t.editor.feedback.imported(files.map((file) => file.name).join(', ')));
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error instanceof ApiError && error.code === 'NO_TEXT_FOUND' && imageMode === 'text') {
        // An image with no text to read, such as a photo, can still be described.
        setChoice({
          files,
          draft,
          range,
          source: extractImageText,
          account: importAccount,
          noText: true,
        });
        return;
      }
      notify(error instanceof ApiError ? describeError(error) : t.editor.feedback.importFailed);
    } finally {
      if (importSession.current === controller) {
        importSession.current = null;
        setImporting(null);
      }
    }
  }

  async function paste() {
    const field = fieldRef.current;
    if (!field) return;
    const draft = field.value;
    const start = field.selectionStart;
    const end = field.selectionEnd;
    setPasting(true);
    try {
      const text = await (readClipboard ? readClipboard() : navigator.clipboard.readText());
      if (fieldRef.current !== field) return;
      if (field.value !== draft) {
        notify(t.editor.feedback.pasteChanged);
        return;
      }
      if (!text) {
        notify(t.editor.feedback.clipboardEmpty);
        return;
      }
      // The pasted text comes up into the draft, and the caret goes after it once it's there.
      textIn(draft, draft.slice(0, start) + text + draft.slice(end), 'paste');
      withViewTransition(
        'paste',
        () => {
          onChange(draft.slice(0, start) + text + draft.slice(end));
        },
        {
          after: () => {
            requestAnimationFrame(() => {
              if (fieldRef.current !== field) return;
              field.focus();
              field.setSelectionRange(start + text.length, start + text.length);
            });
          },
        },
      );
      notify(t.editor.feedback.pasted);
    } catch {
      notify(t.editor.feedback.pasteUnavailable(`${modifierKey()}+V`));
      field.focus();
    } finally {
      setPasting(false);
    }
  }

  return (
    <section
      className={cx('input-panel', expanded && 'input-panel--expanded')}
      aria-labelledby="input-heading"
    >
      <div ref={headerRef} className="section-header">
        <h2 className="section-title" id="input-heading">
          {t.editor.heading}
        </h2>
        {heading}
        <div className="section-actions">
          {headerActions}
          {/* Named as their labels are, which make way for their icons when short of room. */}
          {onShowShortcuts && (
            <Button
              className="editor-shortcuts"
              icon="keyboard"
              aria-label={t.editor.shortcuts}
              aria-keyshortcuts={ariaShortcut(shortcuts.showShortcuts, platform)}
              title={`${t.shortcuts.title} (${formatShortcut(shortcuts.showShortcuts, platform)})`}
              onClick={onShowShortcuts}
            >
              {t.editor.shortcuts}
            </Button>
          )}
          {showExpandButton && (
            <Button
              className="editor-expand"
              icon={expanded ? 'collapse' : 'expand'}
              aria-label={t.editor.expand}
              aria-keyshortcuts={
                expanded
                  ? `${ariaShortcut(shortcuts.fullScreen, platform)} Escape`
                  : ariaShortcut(shortcuts.fullScreen, platform)
              }
              title={
                expanded
                  ? `${t.editor.collapse} (${formatShortcut(shortcuts.fullScreen, platform)}, Esc)`
                  : `${t.editor.expand} (${formatShortcut(shortcuts.fullScreen, platform)})`
              }
              aria-pressed={expanded}
              aria-controls="input-editor"
              onClick={() => {
                if (onExpandedChange) onExpandedChange(!expanded);
                else setExpanded(!expanded);
              }}
            >
              {t.editor.expand}
            </Button>
          )}
        </div>
      </div>

      <form
        id="input-editor"
        className="editor"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        {toolbar}
        <span className="sr-only" id="input-text-label">
          {t.editor.textLabel}
        </span>
        <WritingField
          fieldRef={fieldRef}
          aids={writingAids.settings}
          mark={mark ?? imageMark}
          formatting={formatting}
          id="input-text"
          className="textarea"
          aria-labelledby="input-text-label"
          placeholder={t.editor.placeholder}
          value={value}
          readOnly={readOnly}
          aria-invalid={overLimit || undefined}
          aria-describedby={overLimit ? 'input-counts input-limit-message' : 'input-counts'}
          onChange={(next, typing) => {
            // Typing brings a letter at a time; more at once is a paste, a drop, or a suggestion.
            textIn(value, next, next.length - value.length > 1 ? 'paste' : 'type');
            onChange(next, typing);
          }}
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing || event.altKey || !(event.metaKey || event.ctrlKey))
              return;
            const key = event.key.toLowerCase();
            if (key === 'z' || (event.ctrlKey && key === 'y')) {
              event.preventDefault();
              if (event.shiftKey || key === 'y') travel(onRedo, 'redo');
              else travel(onUndo, 'undo');
            }
          }}
          onUndo={() => {
            travel(onUndo, 'undo');
          }}
          onRedo={() => {
            travel(onRedo, 'redo');
          }}
          onFiles={(files, at) => {
            void importFiles(files, at);
          }}
          pageWide={pageWide}
        />

        {/*
          Before images are sent: read their text, or describe them. The two weigh the same, so
          neither is put first: T reads, D describes, and Esc cancels.
        */}
        {isChoosing && (
          <div
            ref={choiceRef}
            className="editor-import-status image-choice"
            role="group"
            aria-label={t.editor.imageChoice}
            tabIndex={-1}
            onKeyDown={(event) => {
              if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                cancelChoice();
                return;
              }
              const mode = IMAGE_KEYS[event.key.toLowerCase()];
              if (!mode || (mode === 'text' && choice.noText)) return;
              event.preventDefault();
              choose(mode);
            }}
          >
            <p className={cx('image-choice-file', choice.noText && 'is-warning')}>
              {choice.noText || !choiceImages[0] ? (
                <Icon name={choice.noText ? 'alert' : 'image'} />
              ) : (
                <ImageThumb file={choiceImages[0]} />
              )}
              {choice.noText ? (
                <span role="status">{t.editor.noTextFound}</span>
              ) : (
                // Several images: how many, with their names when pointed at.
                <FileName
                  names={choiceImages.map((file) => file.name)}
                  label={choiceImages.length > 1 ? t.counts.images(choiceImages.length) : undefined}
                />
              )}
            </p>
            <div className="image-choice-actions">
              <div className="image-choice-options">
                {IMAGE_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className="button image-choice-option"
                    data-mode={mode}
                    aria-keyshortcuts={IMAGE_MODE_KEYS[mode]}
                    title={`${mode === 'text' ? t.editor.readText : t.editor.describeImage} (${IMAGE_MODE_KEYS[mode]})`}
                    // An image with no text keeps its place for reading, out of use.
                    disabled={mode === 'text' && choice.noText}
                    onClick={() => {
                      choose(mode);
                    }}
                  >
                    <Icon name={mode === 'text' ? 'read-text' : 'describe-image'} />
                    {mode === 'text' ? t.editor.readText : t.editor.describeImage}
                  </button>
                ))}
              </div>
              <span className="image-choice-cost">
                {format.number(imageCredits)}
                {t.account.chipUnit(imageCredits)}
              </span>
              <button
                type="button"
                className="button button--ghost icon-button"
                aria-label={t.common.cancel}
                title={`${t.common.cancel} (Esc)`}
                onClick={cancelChoice}
              >
                <Icon name="close" />
              </button>
            </div>
          </div>
        )}
        {isImporting && importing.image && (
          <ImageProgress
            mode={importing.mode ?? 'text'}
            file={importing.file}
            fileName={importing.fileName}
            from={importing.from}
            pillRef={progressRef}
            onCancel={cancelImport}
          />
        )}
        {notice && <Toast key={notice.id} message={notice.message} onDismiss={dismissNotice} />}
        {overLimit && (
          <p className="editor-limit-message" id="input-limit-message">
            {t.editor.removeCharacters(characters - MAX_TEXT_LENGTH)}
          </p>
        )}
        {/*
          Reading aloud shows on Read aloud's own button, in place of a line of text that would
          make the editor taller; what it's doing is still said, unseen, for screen readers. A
          failure shows in a note over the bottom of the draft.
        */}
        {speech && (
          <div className="editor-speech">
            <p className={speech.status === 'error' ? 'speech-note' : 'sr-only'} role="status">
              {speech.status === 'error'
                ? speech.error instanceof ApiError
                  ? describeError(speech.error)
                  : t.editor.readAloudFailed
                : speech.status === 'loading'
                  ? t.speech.loading
                  : t.speech.aiVoice}
            </p>
          </div>
        )}

        <div ref={toolsRef} className="editor-tools" role="group" aria-label={t.editor.tools}>
          {dictation}
          {aiTools}
          <WritingAids aids={writingAids.aids} />
          <div className="editor-tool-actions">
            <button
              type="button"
              className="button button--ghost icon-button editor-tool-icon"
              aria-label={t.editor.undo}
              title={t.editor.undoTitle(`${modifierKey()}+Z`)}
              disabled={!canUndo}
              data-tool="undo"
              onClick={() => {
                travel(onUndo, 'undo');
              }}
            >
              <Icon name="undo" />
            </button>
            <button
              type="button"
              className="button button--ghost icon-button editor-tool-icon"
              aria-label={t.editor.redo}
              title={t.editor.redoTitle(`${modifierKey()}+Shift+Z`)}
              disabled={!canRedo}
              data-tool="redo"
              onClick={() => {
                travel(onRedo, 'redo');
              }}
            >
              <Icon name="redo" />
            </button>
            <button
              type="button"
              className="button button--ghost icon-button editor-tool-icon"
              aria-label={t.editor.importFile}
              title={`${t.editor.importTitle}. ${t.editor.imageTextHint(`${format.number(CREDIT_RATES.imagePerImage)}${t.account.chipUnit(CREDIT_RATES.imagePerImage)}`)}`}
              disabled={readOnly || isImporting || isChoosing}
              data-tool="import"
              onClick={() => {
                playTool(toolsRef.current, 'import');
                fileRef.current?.click();
              }}
            >
              <Icon name="upload" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept={IMPORT_ACCEPT}
              multiple
              hidden
              aria-label={t.editor.fileLabel}
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = '';
                void importFiles(files);
              }}
            />
            <button
              type="button"
              className="button button--ghost icon-button editor-tool-icon"
              aria-label={t.editor.paste}
              title={t.editor.pasteTitle(`${modifierKey()}+V`)}
              disabled={readOnly || pasting}
              data-tool="paste"
              onClick={() => {
                playTool(toolsRef.current, 'paste');
                void paste();
              }}
            >
              <Icon name="paste" />
            </button>
            <button
              type="button"
              className={cx(
                'button button--ghost icon-button editor-tool-icon',
                clipboard.status?.outcome === 'copied' && 'is-copied',
              )}
              aria-label={t.editor.copy}
              title={t.editor.copyTitle}
              disabled={!value}
              data-tool="copy"
              onClick={() => {
                playTool(toolsRef.current, 'copy');
                void clipboard.copy('draft', value).then((outcome) => {
                  notify(
                    outcome === 'copied' ? t.editor.feedback.copied : t.editor.feedback.copyFailed,
                  );
                });
              }}
            >
              <Icon name={clipboard.status?.outcome === 'copied' ? 'check' : 'copy'} />
            </button>
            {onSpeak && (
              <button
                type="button"
                className={cx(
                  'button button--ghost icon-button editor-tool-icon speak-button',
                  speaking && 'is-speaking',
                )}
                aria-label={speaking ? t.editor.stopReading : t.editor.readAloud}
                title={
                  speaking
                    ? t.editor.stopReading
                    : `${t.editor.readAloud} · ${t.speech.aiVoice} · ${format.number(speechCredits)}${t.account.chipUnit(speechCredits)}`
                }
                aria-pressed={speaking}
                disabled={!speaking && (readOnly || !value.trim())}
                data-tool="speak"
                onClick={() => {
                  playTool(toolsRef.current, 'speak');
                  (speaking ? onStopSpeech : onSpeak)?.();
                }}
              >
                {speaking ? (
                  // The voice, as a little spectrum; pointed at or focused, Stop, which it is.
                  <>
                    <VoiceWave status={speech.status} />
                    <Icon name="stop" />
                  </>
                ) : (
                  <Icon name="speaker" />
                )}
              </button>
            )}
            {/* The draft, and its versions where Clear takes them too; Undo brings the text back. */}
            <button
              type="button"
              className="button button--ghost icon-button editor-tool-icon"
              aria-label={t.editor.clear}
              aria-keyshortcuts={ariaShortcut(shortcuts.clearDraft, platform)}
              title={`${t.editor.clear} (${formatShortcut(shortcuts.clearDraft, platform)})`}
              disabled={readOnly || (!value && !clearsResults)}
              data-tool="clear"
              onClick={() => {
                playTool(toolsRef.current, 'clear');
                onClear();
              }}
            >
              <Icon name="trash" />
            </button>
          </div>
        </div>

        <div className="editor-footer">
          <p className="editor-stats" id="input-counts">
            <span className={cx('usage-meter', overLimit && 'is-over-limit')} aria-hidden="true">
              <span className="usage-meter-fill" style={{ width: `${usage * 100}%` }} />
            </span>
            <span className={cx('character-count', overLimit && 'is-over-limit')}>
              <RollingNumber value={format.number(characters)} /> / {format.number(MAX_TEXT_LENGTH)}
              <span className="character-count-label"> {t.editor.charactersUnit}</span>
            </span>
            <span className={cx('word-count', overAllowance && 'is-over-limit')}>
              <RollingNumber value={t.counts.words(words)} />
              {overAllowance && showCreditCounter && (
                <>
                  {' · '}
                  <CreditsLeft credits={creditsLeft} />
                </>
              )}
            </span>
          </p>
          {actions}
          <Button
            ref={submitRef}
            type="submit"
            variant="primary"
            icon="sparkle"
            className="editor-submit"
            shortcut={waiting ? undefined : formatShortcut(shortcuts.rewrite, platform)}
            aria-keyshortcuts={ariaShortcut(shortcuts.rewrite, platform)}
            aria-busy={busy}
            disabled={!canSubmit}
          >
            {/* The countdown's digits roll from one second to the next. */}
            <span key={String(waiting)} className="editor-submit-label">
              {waiting ? <RollingNumber value={submitLabel} /> : submitLabel}
            </span>
          </Button>
        </div>
      </form>
    </section>
  );
}
